import type { SponsorStatement, StatementLine } from './economicsModel'

/**
 * What the Sponsor's Operating Statement chart reads off the statement.
 *
 * The chart used to plot ONE line, picked from a list, on a scale from zero: a
 * five-year revenue line read as flat and said nothing an underwriter needed.
 * The statement holds the whole case — revenue, expenses, NOI, debt service and
 * coverage — so the chart now shows how they move together, and the handful of
 * figures an underwriter derives from them first: growth, margin, expense ratio
 * and coverage.
 *
 * Every number here is either a value the sponsor's workbook states, or a ratio
 * computed from two stated values and labelled as computed. Nothing is filled
 * in: a period a line leaves blank stays a gap, and a figure that would need a
 * missing input is not shown.
 */

export type LineUnit = 'money' | 'multiple' | 'percent' | 'ratio'

export type ChartSeries = {
  /** Stable per line of this statement, so a series keeps its color and its
   *  toggle whatever else is shown. */
  id: string
  label: string
  key: string | null
  unit: LineUnit
  color: string
  values: Array<number | null>
  cells: string[]
}

/**
 * The series colors, in the order they were validated together on the dossier's
 * dark surface (#0d0d0d) with the data-viz palette checks: lightness band,
 * chroma floor, adjacent-pair color-blind separation (worst ΔE 8.4), the
 * normal-vision floor and 3:1 contrast all pass. Assigned to a line by what the
 * line IS (below), never by its rank, so hiding one series never repaints the
 * others.
 */
export const SERIES_COLORS = ['#3987e5', '#d95926', '#199e70', '#c98500', '#9085e9', '#d55181'] as const

/** Which slot a canonical line always takes. NOI takes the gold slot: it is the
 *  figure the rest of the statement exists to explain. */
const SLOT_BY_KEY: Record<string, number> = { egi: 0, opex: 1, debtService: 2, noi: 3, cashFlow: 4 }

const MONEY_KEYS = new Set(['egi', 'opex', 'noi', 'cashFlow', 'debtService'])

/** The canonical lines the chart opens on, when the statement states them. */
const DEFAULT_KEYS = ['egi', 'opex', 'debtService', 'noi']

/** Underwriting abbreviations stay capitalized however the model wrote them. */
const ACRONYMS = /\b(noi|egi|dscr|cfads|gpr|ltv|ltc|capex|cam|rubs|ti|lc|ffe|t-?12)\b/gi

export function titleCase(label: string): string {
  return label
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    .replace(ACRONYMS, (m) => m.toUpperCase())
}

/** What a line is called where space is short — the end-of-line label. A
 *  canonical line has a plain name; any other keeps the sponsor's own words. */
const SHORT_BY_KEY: Record<string, string> = {
  egi: 'Revenue', opex: 'Expenses', debtService: 'Debt service', noi: 'NOI', cashFlow: 'Cash flow',
}

export function shortLabel(series: { key: string | null; label: string }): string {
  const short = series.key ? SHORT_BY_KEY[series.key] : undefined
  if (short) return short
  return series.label.length > 16 ? `${series.label.slice(0, 15)}…` : series.label
}

function stated(values: Array<number | null>): number[] {
  return values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
}

/** A line's unit. A canonical key says it; otherwise scale is the only honest
 *  signal (the model does not say which column is a ratio), as the table's own
 *  formatter already assumes. */
export function unitOf(line: StatementLine): LineUnit {
  if (line.key === 'dscr') return 'multiple'
  if (line.key === 'debtYield') return 'percent'
  if (line.key && MONEY_KEYS.has(line.key)) return 'money'
  const values = stated(line.values)
  if (values.length && values.every((v) => Math.abs(v) <= 10)) return 'ratio'
  return 'money'
}

export type StatementSeries = {
  /** The dollar lines the chart can draw, each with its fixed color. */
  money: ChartSeries[]
  /** Dollar lines past the six colors the chart can tell apart; the Table view
   *  still shows them. */
  overflow: number
  /** Open on these. */
  defaults: string[]
}

export function buildSeries(statement: SponsorStatement): StatementSeries {
  const money: ChartSeries[] = []
  const used = new Set<number>()
  const extras: StatementLine[] = []
  statement.lines.forEach((line, index) => {
    if (unitOf(line) !== 'money' || !stated(line.values).length) return
    const slot = line.key != null ? SLOT_BY_KEY[line.key] : undefined
    if (slot != null && !used.has(slot)) {
      used.add(slot)
      money.push(series(line, index, slot))
    } else {
      extras.push(line)
    }
  })
  let overflow = 0
  for (const line of extras) {
    const slot = SERIES_COLORS.findIndex((_, i) => !used.has(i))
    if (slot < 0) {
      overflow += 1
      continue
    }
    used.add(slot)
    money.push(series(line, statement.lines.indexOf(line), slot))
  }
  money.sort((a, b) => SERIES_COLORS.indexOf(a.color as never) - SERIES_COLORS.indexOf(b.color as never))
  const canonical = DEFAULT_KEYS.map((key) => money.find((s) => s.key === key)?.id).filter(Boolean) as string[]
  const defaults = canonical.length ? canonical : money.slice(0, 3).map((s) => s.id)
  return { money, overflow, defaults }
}

function series(line: StatementLine, index: number, slot: number): ChartSeries {
  return {
    id: `line-${index}`,
    label: titleCase(line.label),
    key: line.key,
    unit: 'money',
    color: SERIES_COLORS[slot],
    values: line.values,
    cells: line.cells,
  }
}

function lineByKey(statement: SponsorStatement, key: string): StatementLine | undefined {
  return statement.lines.find((line) => line.key === key && stated(line.values).length)
}

/** Change from the previous period, as a fraction; null when either is missing
 *  or the base is zero. */
export function periodChange(values: Array<number | null>, index: number): number | null {
  if (index < 1) return null
  const now = values[index]
  const before = values[index - 1]
  if (now == null || before == null || before === 0) return null
  return (now - before) / Math.abs(before)
}

/** Each value as an index of the line's first stated value (= 100). */
export function indexed(values: Array<number | null>): Array<number | null> {
  const first = values.find((v) => v != null && v !== 0)
  if (first == null) return values.map(() => null)
  return values.map((v) => (v == null ? null : (v / first) * 100))
}

export type Growth = { value: number; basis: string; kind: 'cagr' | 'change' }

/** Growth from a line's first stated period to its last: compounded per year on
 *  an annual statement, total change otherwise. */
export function growthOf(values: Array<number | null>, periods: string[], cadence: string): Growth | null {
  const firstIndex = values.findIndex((v) => v != null)
  let lastIndex = -1
  values.forEach((v, i) => { if (v != null) lastIndex = i })
  if (firstIndex < 0 || lastIndex <= firstIndex) return null
  const first = values[firstIndex] as number
  const last = values[lastIndex] as number
  if (first <= 0 || last <= 0) return null
  const span = `${periods[firstIndex]} → ${periods[lastIndex]}`
  if (cadence === 'annual') {
    const years = lastIndex - firstIndex
    return { value: (last / first) ** (1 / years) - 1, basis: `CAGR · ${span}`, kind: 'cagr' }
  }
  return { value: last / first - 1, basis: `Change · ${span}`, kind: 'change' }
}

export type CoveragePoint = { period: string; dscr: number | null; noi: number | null; debtService: number | null }

export type Coverage = {
  points: CoveragePoint[]
  /** The workbook states the ratio, or it is NOI ÷ debt service from stated lines. */
  basis: 'stated' | 'computed'
  cell: (index: number) => string | null
}

export function coverageOf(statement: SponsorStatement): Coverage | null {
  const dscr = lineByKey(statement, 'dscr')
  const noi = lineByKey(statement, 'noi')
  const ds = lineByKey(statement, 'debtService')
  const points = statement.periods.map((period, i) => {
    const n = noi?.values[i] ?? null
    const d = ds?.values[i] ?? null
    const statedRatio = dscr?.values[i] ?? null
    const computed = n != null && d != null && d > 0 ? n / d : null
    return { period, dscr: dscr ? statedRatio : computed, noi: n, debtService: d }
  })
  if (!points.some((p) => p.dscr != null)) return null
  return {
    points,
    basis: dscr ? 'stated' : 'computed',
    cell: (i) => (dscr ? dscr.cells[i] || null : null),
  }
}

export type Kpi = {
  id: string
  label: string
  value: string
  basis: string
  /** Shown beside the value: computed from stated lines, or stated. */
  source: 'computed' | 'stated'
  tone?: 'warn'
}

function pct(value: number, digits = 1): string {
  return `${(value * 100).toFixed(digits)}%`
}

function lastWith(statement: SponsorStatement, a: StatementLine, b: StatementLine): number {
  for (let i = statement.periods.length - 1; i >= 0; i -= 1) {
    if (a.values[i] != null && b.values[i] != null && (b.values[i] as number) !== 0) return i
  }
  return -1
}

/** The figures an underwriter derives from the statement first, each only when
 *  every input is stated. */
export function kpisOf(statement: SponsorStatement): Kpi[] {
  const out: Kpi[] = []
  const revenue = lineByKey(statement, 'egi')
  const noi = lineByKey(statement, 'noi')
  const opex = lineByKey(statement, 'opex')
  const growthLabel = statement.cadence === 'annual' ? 'growth / yr' : 'change'
  if (revenue) {
    const g = growthOf(revenue.values, statement.periods, statement.cadence)
    if (g) out.push({ id: 'revenue-growth', label: `Revenue ${growthLabel}`, value: pct(g.value), basis: g.basis, source: 'computed' })
  }
  if (noi) {
    const g = growthOf(noi.values, statement.periods, statement.cadence)
    if (g) out.push({ id: 'noi-growth', label: `NOI ${growthLabel}`, value: pct(g.value), basis: g.basis, source: 'computed' })
  }
  if (noi && revenue) {
    const i = lastWith(statement, noi, revenue)
    if (i >= 0) {
      const margin = (noi.values[i] as number) / (revenue.values[i] as number)
      out.push({ id: 'noi-margin', label: 'NOI margin', value: pct(margin), basis: `NOI ÷ revenue · ${statement.periods[i]}`, source: 'computed' })
    }
  }
  if (opex && revenue) {
    const i = lastWith(statement, opex, revenue)
    if (i >= 0) {
      const ratio = Math.abs(opex.values[i] as number) / (revenue.values[i] as number)
      out.push({ id: 'expense-ratio', label: 'Expense ratio', value: pct(ratio), basis: `Expenses ÷ revenue · ${statement.periods[i]}`, source: 'computed' })
    }
  }
  const coverage = coverageOf(statement)
  if (coverage) {
    let min: CoveragePoint | null = null
    for (const p of coverage.points) if (p.dscr != null && (min == null || p.dscr < (min.dscr as number))) min = p
    if (min && min.dscr != null) {
      out.push({
        id: 'dscr-min',
        label: 'Lowest DSCR',
        value: `${min.dscr.toFixed(2)}x`,
        basis: `${min.period} · ${coverage.basis === 'stated' ? 'as stated' : 'NOI ÷ debt service'}`,
        source: coverage.basis,
        tone: min.dscr < 1 ? 'warn' : undefined,
      })
    }
  }
  return out
}
